/**
 * Central registry of data-testid values used for e2e tests.
 *
 * Single source of truth so the app and the test suite
 * stay in sync. Grouped by flow. Static values live in `testIds`; dynamic /
 * templated ids are produced by the helpers below.
 *
 * Shared across clients where the UI matches. Client-specific selectors live
 * beside the shared flow they extend (for example, Kihnu vehicle inventory).
 */
export const testIds = {
  header: {
    upperBar: 'upper-bar',
    navLogout: 'top-nav-/auth/logout',
    navProfileSettings: 'top-nav-/profile/settings',
    mobileMenu: 'top-menu-mobile',
  },

  search: {
    where: 'searchBarWhere',
    whenWrapper: 'when-wrapper',
    roundTripCheckbox: 'round-trip-checkbox',
    goTickets: 'go-tickets',
    route: 'route',
    calendarBack: 'calendar-back',
    calendarForward: 'calendar-forward',
  },

  sailSelect: {
    ticketCard: 'ticket-card',
    departureTime: 'departure-time',
    travelTime: 'travel-time',
    routeName: 'route-name',
    selectedDate: 'selected-date',
    passengerAvailabilityAmount: 'Passenger-availability-amount',
    bikeAvailabilityAmount: 'Bike-availability-amount',
    passengerReservedAmount: 'Passenger-reserved-amount',
    carAvailabilityAmount: 'Car-availability-amount',
    carReservedAmount: 'Car-reserved-amount',
    oneWay: 'one-way',
    addReturnRouteButton: 'add-return-route-button',
    changeDirectionButton: 'change-direction-button',
    selectDateButton: 'select-date-button',
    clockIcon: 'clock-icon',
    infoIcon: 'info-icon',
    passengerIconHeader: 'passenger-icon-header',
    residentIconHeader: 'resident-icon-header',
    bicycleIconHeader: 'bicycle-icon-header',
    vehicleIconHeader: 'vehicle-icon-header',
    residentVehicleIconHeader: 'resident-vehicle-icon-header',
    dangerGoodsIcon: 'danger-goods-icon',
    noHeavyTrucksIcon: 'no-heavy-trucks-icon',
    cancelledIcon: 'cancelled-icon',
    noteAboutDangerous: 'note-about-dangerous',
    noteAboutNoHeavyTrucks: 'note-about-no-heavy-trucks',
    noteAboutCancelled: 'note-about-cancelled',
    noteAboutSoldOut: 'note-about-sold-out',
    noSails: 'no-sails',
    goToNext: 'go-to-next',
    bookingContainer: 'booking-container',
    scheduleWrapper: 'schedule-wrapper',
    departureCard: 'departure-card',
    departureBackCard: 'departure-back-card',
  },

  // BACK / CONTINUE pair under the booking steps; CONTINUE is `sailSelect.goToNext`
  bottomNav: {
    container: 'bottom-slider',
    goToPrev: 'go-to-prev',
    goBackNext: 'go-back-next',
  },

  tickets: {
    categoryTitle: 'category-title',
    categoryDescription: 'category-description',
    categoryPrice: 'category-price',
    categorySubtotal: 'category-subtotal',
    buttonsIncDec: 'buttons-inc-dec',
    passengers: 'passengers',
    passengerFormToggle: 'passenger-form-toggle',
    passengerDelete: 'passenger-delete',
    passengerFirstName: 'passenger-first-name',
    passengerLastName: 'passenger-last-name',
    passengerBirthdateDay: 'passenger-birthdate-day',
    passengerBirthdateMonth: 'passenger-birthdate-month',
    passengerBirthdateYear: 'passenger-birthdate-year',
    passengerCitizenship: 'passenger-citizenship',
    passengerGender: 'passenger-gender',
    passengerExtraInfo: 'passenger-extra-info',
    inputLocalId: 'input-local-id',
    checkLocalId: 'check-local-id',
    residentOverrideCheckbox: 'resident-override-checkbox',
    residentOverrideError: 'resident-override-error',
    passengerSave: 'passenger-save',
    passengerCancel: 'passenger-cancel',
    deleteTicketButton: 'delete-ticket-button',
    cartCard: 'cart-card',
    cartItemCategory: 'cart-item-category',
    cartItemPrice: 'cart-item-price',
    cartItemQuantity: 'cart-item-quantity',
    cartItemSubtotal: 'cart-item-subtotal',
    totalToPay: 'total-to-pay',
  },

  confirm: {
    bookWrapper: 'book-wrapper',
    logInLink: 'confirm-log-in-link',
    createAccountCheckbox: 'confirm-create-account-checkbox',
    customerInputFirstName: 'customer-input-first-name',
    customerInputLastName: 'customer-input-last-name',
    customerInputEmail: 'customer-input-email',
    customerInputPhoneCode: 'customer-input-phone-code',
    customerInputPhoneNumber: 'customer-input-phone-number',
    selectTicketsUsingAs: 'select-tickets-using-as',
    selectRolePrivate: 'select-role-private',
    selectRoleCompany: 'select-role-company',
    companyDetailsToggle: 'company-details-toggle',
    companyLegalName: 'company-legal-name',
    companyRegNumber: 'company-reg-number',
    companyLegalAddress: 'company-legal-address',
    companyZip: 'company-zip',
    companyCity: 'company-city',
    companyCountry: 'company-country',
    paymentMethods: 'payment-methods',
    paymentTerms: 'payment-terms',
    paymentTermsCheckbox: 'payment-terms-checkbox',
  },

  printedTicket: {
    passengerInfo: 'printed-ticket-passenger-info',
    innerTicket: 'inner-ticket',
    infoLeg1: 'ticket-info-leg-1',
    infoLeg2: 'ticket-info-leg-2',
    infoHeader: 'ticket-info-header',
    qrCode: 'qr-code',
  },

  /**
   * Edit-reservation flow. Kept as a separate namespace from the purchase flow
   * even where the UI looks identical, because the edit screens are their own
   * component copies (PaymentSection, EditCartCard, EditReservationCustomerTab).
   * All values are prefixed `edit-` so the two selector sets never collide.
   */
  edit: {
    // Step tabs (ROUTE / TICKETS / CUSTOMER)
    tabRoute: 'edit-tab-route',
    tabTickets: 'edit-tab-tickets',
    tabCustomer: 'edit-tab-customer',
    // Direction (round-trip) tabs inside the TICKETS tab
    directionOutbound: 'edit-direction-outbound',
    directionReturn: 'edit-direction-return',
    // Route-tab journey block (calendar + sail rows), one per direction
    journeySection: 'edit-journey-section',
    // Selected-departure summary shown beside the calendar (route tab)
    routeName: 'edit-route-name',
    selectedDate: 'edit-selected-date',
    departureTime: 'edit-departure-time',
    // Bottom actions
    cancel: 'edit-cancel',
    finish: 'edit-finish',
    paymentCancel: 'edit-payment-cancel',
    pay: 'edit-pay',
    // Payment section (extra-payment step)
    paymentMethods: 'edit-payment-methods',
    paymentTerms: 'edit-payment-terms',
    paymentTermsCheckbox: 'edit-payment-terms-checkbox',
    creditUnavailableHint: 'edit-credit-unavailable-hint',
    // Cart
    cartCard: 'edit-cart-card',
    // Selected route/date/time per sail group (update when the route is edited)
    cartRouteName: 'edit-cart-route-name',
    cartDate: 'edit-cart-date',
    cartTime: 'edit-cart-time',
    cartItemCategory: 'edit-cart-item-category',
    cartItemPrice: 'edit-cart-item-price',
    cartItemQuantity: 'edit-cart-item-quantity',
    cartItemSubtotal: 'edit-cart-item-subtotal',
    cartVehiclePlate: 'edit-cart-vehicle-plate',
    cartResidentIds: 'edit-cart-resident-ids',
    cartDriverDiscount: 'edit-cart-driver-discount',
    cartTotal: 'edit-cart-total',
    cartPreviouslyPaid: 'edit-cart-previously-paid',
    cartPriceDifference: 'edit-cart-price-difference',
    // Customer tab — summary shown above the form (updates after save)
    customerSummaryName: 'edit-customer-summary-name',
    customerSummaryPhone: 'edit-customer-summary-phone',
    customerSummaryEmail: 'edit-customer-summary-email',
    // Customer tab (contact form)
    customerFirstName: 'edit-customer-first-name',
    customerLastName: 'edit-customer-last-name',
    customerPhoneCode: 'edit-customer-phone-code',
    customerPhoneNumber: 'edit-customer-phone-number',
    customerEmail: 'edit-customer-email',
    customerReset: 'edit-customer-reset',
    customerSave: 'edit-customer-save',
    // Session expiry banner + prolong modal
    sessionBanner: 'edit-session-banner',
    sessionExpiredOk: 'edit-session-expired-ok',
    sessionEnd: 'edit-session-end',
    sessionProlong: 'edit-session-prolong',
    // Unsaved-changes (tab switch) modal
    unsavedDiscard: 'edit-unsaved-discard',
    unsavedStay: 'edit-unsaved-stay',
  },

  /**
   * Vehicle/trailer ticket editing (kihnu). One card per VEHICLE/TRAILER subtype,
   * see `testIdHelpers.vehicleCard`.
   */
  vehicle: {
    expandTile: 'vehicle-expand-tile',
    plateInput: 'vehicle-plate-input',
    lookupStatus: 'vehicle-lookup-status',
    registryPanel: 'vehicle-registry-panel',
    manualSummary: 'vehicle-manual-summary',
    manualNotice: 'vehicle-manual-notice',
    coefficients: 'vehicle-coefficients',
    priceError: 'vehicle-price-error',
    manualForm: 'vehicle-manual-form',
    categorySelect: 'vehicle-category-select',
    companyCodeInput: 'vehicle-company-code-input',
    companyStatus: 'vehicle-company-status',
    accessibleCheckbox: 'vehicle-accessible-checkbox',
    accessibleStatus: 'vehicle-accessible-status',
    addButton: 'vehicle-add-button',
    cancelButton: 'vehicle-cancel-button',
    deleteButton: 'vehicle-delete-button',
    editButton: 'vehicle-edit-button',
    removeButton: 'vehicle-remove-button',
    plateBadge: 'vehicle-plate-badge',
    bookedSpecs: 'vehicle-booked-specs',
    subtotal: 'vehicle-subtotal',
    weightChangeButton: 'vehicle-weight-change-button',
    weightInput: 'vehicle-weight-input',
    weightSaveButton: 'vehicle-weight-save-button',
    weightDiscardButton: 'vehicle-weight-discard-button',
    weightError: 'vehicle-weight-error',
    weightCorrected: 'vehicle-weight-corrected',
  },
} as const

/** Dynamic / templated test ids (built from runtime values). */
export const testIdHelpers = {
  /** Header nav item by route path, e.g. navItem('/auth/login') -> 'top-nav-/auth/login' */
  navItem: (path: string) => `top-nav-${path}`,
  /** Passenger-category stepper by category id ('iconBike' for bikes, else priceCategory.code). */
  category: (id: string) => ({
    card: `${id}-card`,
    plus: `${id}-plus`,
    minus: `${id}-minus`,
    input: `${id}-input`,
  }),
  /** Payment method by code, e.g. paymentMethod('SWEDBANK') -> 'pm-SWEDBANK' */
  paymentMethod: (code: string) => `pm-${code}`,
  /** Edit-flow payment method by code, e.g. editPaymentMethod('SWEDBANK') -> 'edit-pm-SWEDBANK' */
  editPaymentMethod: (code: string) => `edit-pm-${code}`,
  /** App banner (message box) by severity, e.g. messageBox('error') -> 'message-box-error'. Lets tests assert the message type. */
  messageBox: (variant: string) => `message-box-${variant}`,
  /** Vehicle/trailer ticket card by subtype, e.g. vehicleCard('VEHICLE') -> 'vehicle-card-vehicle' */
  vehicleCard: (subType: 'VEHICLE' | 'TRAILER') => `vehicle-card-${subType.toLowerCase()}`,
  /** Manual vehicle/trailer width or height range, scoped by card. */
  vehicleManualSizeOption: (scope: string, dimension: 'width' | 'height', rangeId: string) =>
    `vehicle-manual-${scope}-${dimension}-${rangeId}`,
  /** Manual vehicle/trailer length or weight stepper controls, scoped by card. */
  vehicleManualStepper: (scope: string, dimension: 'length' | 'weight') => ({
    decrease: `vehicle-manual-${scope}-${dimension}-decrease`,
    input: `vehicle-manual-${scope}-${dimension}-input`,
    increase: `vehicle-manual-${scope}-${dimension}-increase`,
  }),
} as const
